// ExplodeStrategy — the explode-swap seam.
//
// This is the seam that lets the v1 manifest-driven `explosion-graph` (issue
// idiagram#3) replace the v0 geometric heuristic WITHOUT touching the renderer
// or the UI. The renderer (three-renderer.ts) only ever talks to the
// `ExplodeStrategy` interface: it hands the strategy a read-only structural
// view of the loaded assembly (`SceneGraphView`) and gets back a flat list of
// per-node offsets (`ExplodeOffset`). The slider/animation, camera, picking and
// overlay all stay exactly the same regardless of which strategy is plugged in.
//
//   v0:              RadialHierarchyExplode — push each (sub-)assembly's
//                    children outward from its centroid along the nearest axis,
//                    scaled by bbox, recursing the manifest hierarchy. No
//                    solver; matches the ExplodeView baseline.
//   v1 (this file):  ExplosionGraphExplode — read precomputed blocking-aware
//                    directions + escape offsets from the `explosionGraph`
//                    field the converter emits (Li-2008 assembly-by-disassembly
//                    over contact/blocking; see packages/converter/src/
//                    explosion-graph.ts + docs/explosion-graph-schema.md). Same
//                    `computeOffsets()` signature; the renderer is none the
//                    wiser. main.ts makes it the DEFAULT when the manifest
//                    carries an explosion graph, else falls back to v0.
//
// The interface is intentionally renderer-AGNOSTIC: it speaks plain number
// tuples (`Vec3`), never Three.js types, so a Babylon ViewerController could
// reuse the very same strategies. Mirrors the `matchBomToTree()` swap-out seam
// in the converter — heuristic now, smarter later, behind a stable signature.

/** A 3-component vector as a plain tuple. Renderer-agnostic on purpose. */
export type Vec3 = readonly [number, number, number];

/** A signed coordinate axis name, in the manifest's Y-up frame. */
export type AxisName = '+x' | '-x' | '+y' | '-y' | '+z' | '-z';

/** An axis-aligned bounding box in some node's *local* space (i.e. the space
 *  the node's own children live in, before the node's own transform). */
export interface LocalBox {
  min: Vec3;
  max: Vec3;
}

/** The minimal structural + geometric view of the loaded assembly that an
 *  ExplodeStrategy needs. The renderer builds this from the glTF node tree;
 *  a strategy never sees Three.js objects. */
export interface SceneGraphView {
  /** Id of the displayed root (the first "real" assembly node — past the OCCT
   *  outer wrapper). Offsets are computed for its descendants. */
  readonly displayedRootId: string;
  /** Direct child ids of `id`, already filtered to the set the strategy should
   *  treat as movable units at the next level down. The renderer decides the
   *  filtering policy (e.g. drop pure transform wrappers, keep geometry-bearing
   *  synthetic mesh chunks where there is no deeper real hierarchy). */
  childIds(id: string): string[];
  /** Bounding box of the subtree rooted at `id`, expressed in the local space
   *  of `id`'s PARENT — the same space `offsetLocal` is applied in. Returns
   *  null when the subtree has no geometry. */
  boxInParentSpace(id: string): LocalBox | null;
}

/** One node's explode displacement, in the local space of its parent node.
 *  `depth` is the node's hierarchy level below the displayed root (root's
 *  direct children = 0, grandchildren = 1, …). The renderer uses `depth` to
 *  stagger the animation so outermost (deepest) parts move first and nothing
 *  clips through an enclosing part on the way out. */
export interface ExplodeOffset {
  id: string;
  offsetLocal: Vec3;
  depth: number;
}

export interface ExplodeStrategy {
  /** Human-readable id, surfaced in logs / the strategy-swap note. */
  readonly name: string;
  /** Compute the fully-exploded (factor = 1) per-node offsets. The renderer
   *  lerps each node from 0 → its offset as the slider runs 0 → 1. */
  computeOffsets(graph: SceneGraphView): ExplodeOffset[];
  /** Optional (explosion-graph v2, idiagram#6): the Y-up blocker adjacency the
   *  renderer's view-aware targeted reveal needs — node id → { axis → ids that
   *  block it }. Strategies without blocking data (the v0 radial baseline) omit
   *  it, which disables reveal and keeps the plain isolate+frame select. */
  getBlockerMap?(): Map<string, Partial<Record<AxisName, string[]>>>;
}

// ── helpers (pure tuple math; no allocations beyond the returned tuples) ─────

function centerOf(box: LocalBox): Vec3 {
  return [
    (box.min[0] + box.max[0]) / 2,
    (box.min[1] + box.max[1]) / 2,
    (box.min[2] + box.max[2]) / 2,
  ];
}

function sizeOf(box: LocalBox): Vec3 {
  return [box.max[0] - box.min[0], box.max[1] - box.min[1], box.max[2] - box.min[2]];
}

function diagonal(box: LocalBox): number {
  const [sx, sy, sz] = sizeOf(box);
  return Math.hypot(sx, sy, sz);
}

/** Snap a direction to the nearest signed coordinate axis (±X / ±Y / ±Z),
 *  returning a unit tuple. This is the "axis baseline" look — parts fan out
 *  along the model's own axes rather than along arbitrary radial rays, which
 *  reads as cleaner and is what ExplodeView / classic IPC diagrams do. */
function snapToAxis(dir: Vec3): Vec3 {
  const ax = Math.abs(dir[0]);
  const ay = Math.abs(dir[1]);
  const az = Math.abs(dir[2]);
  if (ax >= ay && ax >= az) return [Math.sign(dir[0]) || 1, 0, 0];
  if (ay >= ax && ay >= az) return [0, Math.sign(dir[1]) || 1, 0];
  return [0, 0, Math.sign(dir[2]) || 1];
}

export interface RadialHierarchyExplodeOptions {
  /** Fraction of the (sub-)assembly diagonal each ring of children travels at
   *  full explode, on top of clearing their own half-extent. Larger = airier.
   *  Default 0.6. */
  spread?: number;
  /** When true, snap each child's outward direction to the nearest axis
   *  (ExplodeView look). When false, use the true radial direction from the
   *  centroid. Default true. */
  axisSnap?: boolean;
  /** Each level deeper than the first is scaled by this factor so nested
   *  sub-assembly parts don't fly as far as top-level groups (keeps the
   *  diagram compact). Default 0.55. */
  depthFalloff?: number;
}

/** v0 baseline: hierarchy-aware radial/axis explode. No blocking solver. */
export class RadialHierarchyExplode implements ExplodeStrategy {
  readonly name = 'radial-hierarchy';
  private readonly spread: number;
  private readonly axisSnap: boolean;
  private readonly depthFalloff: number;

  constructor(opts: RadialHierarchyExplodeOptions = {}) {
    this.spread = opts.spread ?? 0.6;
    this.axisSnap = opts.axisSnap ?? true;
    this.depthFalloff = opts.depthFalloff ?? 0.55;
  }

  computeOffsets(graph: SceneGraphView): ExplodeOffset[] {
    const out: ExplodeOffset[] = [];
    this.explodeChildrenOf(graph, graph.displayedRootId, 0, out);
    return out;
  }

  /** Offset every direct child of `parentId` outward from the parent subtree's
   *  centroid, then recurse into each child so sub-assemblies explode their own
   *  children about their own centroid. Offsets are NOT accumulated here — each
   *  node's offset is applied in its parent's local frame, and because a child
   *  is parented under its (already-offset) parent in the scene graph, the
   *  displacements compose automatically at render time. */
  private explodeChildrenOf(
    graph: SceneGraphView,
    parentId: string,
    depth: number,
    out: ExplodeOffset[],
  ): void {
    const childIds = graph.childIds(parentId);
    if (childIds.length === 0) return;

    // Centroid of the whole subtree, in the parent's own local space. We build
    // it from the children's boxes (all already in parent-local space) so the
    // direction we push along is "away from the cluster center".
    const boxes = new Map<string, LocalBox>();
    let cx = 0;
    let cy = 0;
    let cz = 0;
    let n = 0;
    for (const cid of childIds) {
      const box = graph.boxInParentSpace(cid);
      if (!box) continue;
      boxes.set(cid, box);
      const c = centerOf(box);
      cx += c[0];
      cy += c[1];
      cz += c[2];
      n += 1;
    }
    if (n === 0) return;
    const centroid: Vec3 = [cx / n, cy / n, cz / n];

    // Diagonal of the union box ≈ how far the cluster spans; the travel budget.
    const union = unionBoxes(boxes.values());
    const clusterDiag = union ? diagonal(union) : 0;
    const levelScale = Math.pow(this.depthFalloff, depth);

    for (const cid of childIds) {
      const box = boxes.get(cid);
      if (box) {
        const c = centerOf(box);
        let dir: Vec3 = [c[0] - centroid[0], c[1] - centroid[1], c[2] - centroid[2]];
        const len = Math.hypot(dir[0], dir[1], dir[2]);
        if (len < 1e-6) {
          // Child sits on the centroid (concentric part) — nudge it up so it
          // still separates rather than staying buried.
          dir = [0, 1, 0];
        } else {
          dir = [dir[0] / len, dir[1] / len, dir[2] / len];
        }
        if (this.axisSnap) dir = snapToAxis(dir);

        // Travel = clear half the child's own extent along the push axis, plus
        // a spread fraction of the cluster diagonal. Scaled down per level.
        const childHalf = projectedHalfExtent(box, dir);
        const travel = (childHalf + this.spread * clusterDiag) * levelScale;
        out.push({
          id: cid,
          offsetLocal: [dir[0] * travel, dir[1] * travel, dir[2] * travel],
          depth,
        });
      }
      // Recurse: a sub-assembly explodes its own children about its own centroid.
      this.explodeChildrenOf(graph, cid, depth + 1, out);
    }
  }
}

function unionBoxes(boxes: Iterable<LocalBox>): LocalBox | null {
  let min: [number, number, number] | null = null;
  let max: [number, number, number] | null = null;
  for (const b of boxes) {
    if (!min || !max) {
      min = [b.min[0], b.min[1], b.min[2]];
      max = [b.max[0], b.max[1], b.max[2]];
    } else {
      min[0] = Math.min(min[0], b.min[0]);
      min[1] = Math.min(min[1], b.min[1]);
      min[2] = Math.min(min[2], b.min[2]);
      max[0] = Math.max(max[0], b.max[0]);
      max[1] = Math.max(max[1], b.max[1]);
      max[2] = Math.max(max[2], b.max[2]);
    }
  }
  if (!min || !max) return null;
  return { min, max };
}

/** Half the box's extent projected onto a unit direction — how far the box's
 *  own bulk reaches along that axis from its center. */
function projectedHalfExtent(box: LocalBox, unitDir: Vec3): number {
  const [sx, sy, sz] = sizeOf(box);
  return (Math.abs(unitDir[0]) * sx + Math.abs(unitDir[1]) * sy + Math.abs(unitDir[2]) * sz) / 2;
}

// ── v1: ExplosionGraphExplode ────────────────────────────────────────────────

/** One node of the manifest `explosionGraph` field. Mirrors the converter's
 *  ExplosionNode (packages/converter/src/explosion-graph.ts) — duplicated here
 *  so the viewer keeps zero dependency on the converter package, same pattern as
 *  the BOM types mirrored in main.ts. `dir` is a unit axis in the manifest's
 *  Y-up frame (the frame glTF nodes live in), so it's applied directly. `offset`
 *  is the escape distance in metres at full explode. */
export interface ExplosionGraphNode {
  /** idiagram node id — the same id the glTF nodes carry in extras. */
  id: string;
  /** Parent group id, or null at the displayed root. Advisory for the viewer
   *  (offsets are applied in the loaded parent's local frame regardless). */
  parent: string | null;
  /** Explosion direction (unit axis) in the manifest Y-up frame. */
  dir: Vec3;
  /** Escape offset along `dir` at full explode (factor=1), in metres. */
  offset: number;
  /** Greedy removal order: 0 = least-blocked / first off, higher = deeper in. */
  order: number;
  /** Axes the part was still blocked on as-assembled (Y-up frame). Advisory. */
  blockedAxes?: AxisName[];
  /** WHICH groups block this one, per Y-up axis (explosion-graph v2, idiagram#6).
   *  The data the view-aware targeted reveal walks: clicking a part explodes the
   *  parts blocking it along the camera-facing axis (see reveal-solver.ts). Same
   *  Y-up frame as `dir`. Absent on v1 manifests → reveal disabled, the viewer
   *  keeps the isolate+frame select behaviour. */
  blockedBy?: Partial<Record<AxisName, string[]>>;
}

/** v1: manifest-driven, blocking-aware explode. Consumes the converter's
 *  precomputed `explosionGraph` (Li-2008 directions + escape offsets). The
 *  renderer animation, slider, camera + overlay are untouched — this only
 *  changes per-node offsets and the per-node animation order.
 *
 *  Animation order: the converter's greedy `order` (0 = least-blocked, pulled
 *  off first) is mapped to the renderer's `depth` so that the *outermost*
 *  (earliest-removed) part LEADS the explode and the deepest, most-constrained
 *  part trails — the reverse-topological motion from the exploded-view
 *  literature, now driven by real blocking instead of v0's tree depth. */
export interface ExplosionGraphExplodeOptions {
  /** Lateral "outward-fan" nudge added on top of each part's blocking-aware axis
   *  travel, as a fraction of the sibling cluster's diagonal (idiagram#6 cosmetic).
   *  Each part is pushed radially away from its siblings' shared centroid, but only
   *  PERPENDICULAR to its own explosion axis — so the precomputed blocking-aware
   *  travel along `dir` is untouched, while parts that separate along the SAME free
   *  axis (e.g. an AS1 L-bracket riding straight down with the base plate, or its
   *  bolts) still spread apart instead of staying stacked/overlapping. This is the
   *  fan-out look of a classic exploded diagram. 0 disables it (pure axis explode).
   *  Default 0.3 — clears the AS1 plate↔bracket overlap with margin (the overlap
   *  vanishes at 0.25; verified in tools/probes/fan-probe.ts). */
  fan?: number;
}

export class ExplosionGraphExplode implements ExplodeStrategy {
  readonly name = 'explosion-graph';
  private readonly byId: Map<string, ExplosionGraphNode>;
  private readonly maxOrder: number;
  private readonly fan: number;

  constructor(graph: ExplosionGraphNode[], opts: ExplosionGraphExplodeOptions = {}) {
    this.byId = new Map(graph.map((n) => [n.id, n]));
    this.maxOrder = graph.reduce((m, n) => Math.max(m, n.order), 0);
    this.fan = opts.fan ?? 0.3;
  }

  computeOffsets(graph: SceneGraphView): ExplodeOffset[] {
    const out: ExplodeOffset[] = [];
    // Walk the loaded hierarchy from the displayed root and, for every movable
    // unit that has a graph entry, emit its precomputed offset. We walk the
    // SceneGraphView (not the raw graph) so we only move nodes that actually
    // exist as movable units in this load — and so nested sub-assemblies still
    // compose, exactly like the v0 strategy.
    const visit = (parentId: string): void => {
      const childIds = graph.childIds(parentId);
      // Sibling boxes + their shared centroid, in parent-local space (the same
      // frame offsetLocal is applied in) — the basis for the lateral outward fan.
      const boxes = new Map<string, LocalBox>();
      let cx = 0;
      let cy = 0;
      let cz = 0;
      for (const cid of childIds) {
        const box = graph.boxInParentSpace(cid);
        if (!box) continue;
        boxes.set(cid, box);
        const c = centerOf(box);
        cx += c[0];
        cy += c[1];
        cz += c[2];
      }
      const n = boxes.size;
      const centroid: Vec3 = n > 0 ? [cx / n, cy / n, cz / n] : [0, 0, 0];
      const union = unionBoxes(boxes.values());
      const clusterDiag = union ? diagonal(union) : 0;

      for (const cid of childIds) {
        const node = this.byId.get(cid);
        if (node) {
          // offsetLocal = dir * offset. dir is already in the parent's frame
          // (the converter emits in the displayed-root Y-up frame; for the flat
          // part-group level that v1 targets, parent-local == that frame).
          let ox = node.dir[0] * node.offset;
          let oy = node.dir[1] * node.offset;
          let oz = node.dir[2] * node.offset;
          // Lateral outward fan (idiagram#6 cosmetic): push the part radially away
          // from the sibling centroid, with the component ALONG its explosion axis
          // removed, so we add only perpendicular spread and never alter the
          // blocking-aware travel. Magnitude scales by how perpendicular the radial
          // is to `dir` (a part already separating along its radial gets ~none).
          const box = boxes.get(cid);
          if (this.fan > 0 && box && clusterDiag > 0) {
            const c = centerOf(box);
            let rx = c[0] - centroid[0];
            let ry = c[1] - centroid[1];
            let rz = c[2] - centroid[2];
            const rl = Math.hypot(rx, ry, rz);
            if (rl > 1e-9) {
              rx /= rl;
              ry /= rl;
              rz /= rl;
              const along = rx * node.dir[0] + ry * node.dir[1] + rz * node.dir[2];
              const amt = this.fan * clusterDiag;
              ox += (rx - along * node.dir[0]) * amt;
              oy += (ry - along * node.dir[1]) * amt;
              oz += (rz - along * node.dir[2]) * amt;
            }
          }
          // Map removal order → depth so the earliest-removed (least-blocked)
          // part gets the largest depth and therefore LEADS the staggered
          // animation (the renderer gives deeper records a head start).
          const depth = this.maxOrder - node.order;
          out.push({ id: cid, offsetLocal: [ox, oy, oz], depth });
        }
        // Recurse so nested groups still crack open under their parent.
        visit(cid);
      }
    };
    visit(graph.displayedRootId);
    return out;
  }

  /** The Y-up blocker adjacency (explosion-graph v2) for the view-aware targeted
   *  reveal: node id → { axis → ids of the groups blocking it on that axis }.
   *  Returns an empty map when the manifest predates v2 (no node carries
   *  `blockedBy`) — the renderer detects the empty map and keeps the plain
   *  isolate+frame select behaviour instead of moving parts. See reveal-solver.ts. */
  getBlockerMap(): Map<string, Partial<Record<AxisName, string[]>>> {
    const map = new Map<string, Partial<Record<AxisName, string[]>>>();
    for (const n of this.byId.values()) {
      if (n.blockedBy) map.set(n.id, n.blockedBy);
    }
    return map;
  }
}
